import React, { ReactElement, useMemo } from 'react';

import { Platform, Pressable, View } from 'react-native';

import { useTranslation } from 'react-i18next';

import { ArrowRightSmall } from 'assets/images';

import { isTabletOrWeb, isWeb } from 'utils/responsiveness';

import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

interface Props {
    title: string;
    collectionAvailable?: boolean;
    onOpenCollection?(): void;
    testID?: string;
    loading?: boolean;
}

const MAX_NUMBER_OF_LINES_IN_TITLE = 2;

export const StripeHeader = (props: Props): ReactElement => {
    const { t } = useTranslation();
    const {
        title,
        collectionAvailable,
        onOpenCollection,
        testID,
        loading = false,
    } = props;

    const accessibilityLabel = useMemo(() => {
        if (!loading && title) {
            if (collectionAvailable) {
                return t(
                    isWeb
                        ? 'ACCESSIBILITY_%stripe name%_OPEN _COLLECTION_WEB'
                        : 'ACCESSIBILITY_%stripe name%_OPEN _COLLECTION',
                    {
                        'stripe name': title,
                        interpolation: {
                            prefix: '%',
                            suffix: '%',
                            escapeValue: false,
                        },
                    },
                );
            }

            return t('ACCESSIBILITY_%stripe name%_NO_COLLECTION', {
                'stripe name': title,
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            });
        }

        return '';
    }, [loading, title, collectionAvailable, t]);

    const arrowIconProps = useMemo(() => {
        if (isTabletOrWeb) {
            return {
                height: 22,
                viewBox: Platform.OS === 'ios' ? '0 0 14 21' : '0 0 14 18',
            };
        }

        return {
            height: 25,
            viewBox: Platform.OS === 'ios' ? '-4 0 16 16' : '-4 0 16 13',
        };
    }, []);

    if (loading) {
        return (
            <View style={[styles.root, styles.loadingRoot]}>
                <Text
                    category={ETextCategory.title3}
                    color={ETextColor.neutral}
                    numberOfLines={MAX_NUMBER_OF_LINES_IN_TITLE}
                >
                    {' '}
                </Text>
            </View>
        );
    }

    if (!collectionAvailable) {
        return (
            <View
                {...(isWeb
                    ? {
                          role: 'presentation',
                          tabIndex: 0,
                          'aria-label': accessibilityLabel,
                      }
                    : {
                          accessible: true,
                          accessibilityLabel,
                          accessibilityRole: 'text',
                      })}
                style={styles.root}
                testID={testID}
            >
                <Text
                    accessible={false}
                    importantForAccessibility="no-hide-descendants"
                    category={ETextCategory.title3}
                    color={ETextColor.neutral}
                    numberOfLines={MAX_NUMBER_OF_LINES_IN_TITLE}
                >
                    {t(title)}
                </Text>
            </View>
        );
    }

    return (
        <Pressable
            accessible
            accessibilityLabel={accessibilityLabel}
            onPress={onOpenCollection}
            onAccessibilityTap={onOpenCollection}
            accessibilityRole={isWeb ? 'button' : 'none'}
            testID={testID}
            style={styles.root}
        >
            <Text
                accessible={false}
                importantForAccessibility="no-hide-descendants"
                category={ETextCategory.title3}
                color={ETextColor.neutral}
                numberOfLines={MAX_NUMBER_OF_LINES_IN_TITLE}
                style={isWeb && styles.textContainer}
            >
                {t(title)}

                <ArrowRightSmall
                    height={arrowIconProps.height}
                    viewBox={arrowIconProps.viewBox}
                    aria-hidden={isWeb}
                    importantForAccessibility="no-hide-descendants"
                />
            </Text>
        </Pressable>
    );
};
